import { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  getUserList,
  changeUserStatus,
  changeUserPassword,
} from "../../Actions/UserAction";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { IconButton, Tooltip, Box } from "@mui/material";
import { isUndefined, debounce } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import Swal from "sweetalert2";
import ChangePasswordModal from "../drawer/ChangePasswordModal";
import BlockUserModal from "./BlockUserModal";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment/moment";
import { appModeLabels } from "./Variables";

const ViewUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const { statusFromDashboard = "" } = location.state || {};
  const [globalFilter, setGlobalFilter] = useState(statusFromDashboard);
  const [openModalPassword, setOpenPasswordModal] = useState(false);
  const [changingPasswordUserId, setChangingPasswordUserId] = useState(null);
  const [blockingUser, setBlockingUser] = useState(null);

  const {
    userList,
    totalResults,
    isGettingUserList,
    isBlockingUser,
    isChangingUserPassword,
  } = useSelector((state) => state.UserReducer);

  useEffect(() => {
    dispatch(
      getUserList(pagination.pageIndex, pagination.pageSize, globalFilter, statusFromDashboard),
    );
  }, [pagination, dispatch, globalFilter, statusFromDashboard]);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value) => {
        setGlobalFilter(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  const dispatchChangeUserStatus = (id, data, handleClose) => {
    dispatch(
      changeUserStatus(
        id,
        data,
        handleVisible,
        handleDangerVisible,
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        statusFromDashboard,
        handleClose,
      ),
    );
  };

  const handleChangeUserStatus = (row, data) => {
    // Blocking requires a reason, so it is confirmed through BlockUserModal instead.
    if (data.enable === false) {
      setBlockingUser(row.original);
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to Activate this User Record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Activate it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4CAF50",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "mui-swal-popup",
      },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) {
          container.style.zIndex = "2000"; // higher than MUI
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatchChangeUserStatus(row.original._id, {
          ...data,
          blockReason: "",
        });
      }
    });
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 50,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize, // serial number considering pagination
      },
      {
        accessorKey: "userName",
        header: "User Name",
        size: 120,
        grow: false, // fixed width
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          placeholder: "Enter User Name",
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        grow: true, // takes remaining space
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Email",
        },
      },
      {
        accessorKey: "role",
        header: "Role",
        grow: true, // takes remaining space
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Role",
        },
      },
      {
        accessorKey: "lastLogin",
        header: "Last Login",
        grow: true, // takes remaining space
        enableEditing: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "";
          return moment(value).format("DD-MM-YYYY");
        },
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Last Login",
        },
      },
      {
        accessorKey: "allowCompanies",
        header: "Allow Companies",
        grow: true,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Allow Companies",
        },
      },
      {
        accessorKey: "allowInvoices",
        header: "Allow Invoices",
        grow: true,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Allow Invoices",
        },
      },
      {
        accessorKey: "activeDate",
        header: "Active Date",
        grow: true,
        enableEditing: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "";
          return moment(value).format("DD-MM-YYYY");
        },
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Active Date",
        },
      },
      {
        accessorKey: "expireDate",
        header: "Expires Date",
        grow: true,
        enableEditing: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "";
          return moment(value).format("DD-MM-YYYY");
        },
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Expires Date",
        },
      },
      {
        accessorKey: "subscriptionInterval",
        header: "Subscription Interval",
        grow: true,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Subscription Interval",
        },
      },
      {
        accessorKey: "appMode",
        header: "App Mode",
        grow: true,
        enableEditing: false,
        Cell: ({ cell }) => appModeLabels(cell.getValue()),
      },
      {
        id: "actions",
        header: "Actions",
        size: 170,
        grow: false,
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" justifyItems={"center"} alignItems={"center"}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/main/view-user-details?id=${row?.original?._id}`)
                }
                sx={{
                  color: "black",
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Block">
              <IconButton
                size="small"
                onClick={() => handleChangeUserStatus(row, { enable: false })}
                disabled={row?.original?.enable === false}
                sx={{
                  color: "#FA4C2A",
                }}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Activate User">
              <IconButton
                size="small"
                onClick={() => handleChangeUserStatus(row, { enable: true })}
                disabled={row?.original?.enable === true}
                sx={{
                  color: "#4CAF50",
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() =>
                  navigate("/main/edit-user", { state: row.original })
                }
                sx={{ color: "black" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Change Password">
              <IconButton
                size="small"
                onClick={() => {
                  setOpenPasswordModal(true);
                  setChangingPasswordUserId(row.original._id);
                }}
                sx={{ color: "black" }}
              >
                <ChangeCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line
    [pagination],
  );

  const renderMobileUserCards = () => {
    // ✅ Empty state
    if (!userList || userList.length === 0) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "#999",
            fontSize: 14,
          }}
        >
          No Users Found
        </Box>
      );
    }

    return userList.map((item, index) => {
      const serial = index + 1 + pagination.pageIndex * pagination.pageSize;

      return (
        <Box
          key={item._id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {/* Top Row: Serial + Actions */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box sx={{ fontSize: 12, color: "#999" }}>#{serial}</Box>

            <Box display="flex" gap={1} flexWrap="wrap">
              {/* View User */}
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/main/view-user-details?id=${item?._id}`)
                  }
                  sx={{
                    color: "black",
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Block */}
              <Tooltip title="Block">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleChangeUserStatus(
                      { original: item },
                      { enable: false },
                    )
                  }
                  disabled={item?.enable === false}
                  sx={{ color: "black" }}
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Activate */}
              <Tooltip title="Activate User">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleChangeUserStatus({ original: item }, { enable: true })
                  }
                  disabled={item?.enable === true}
                  sx={{ color: "black" }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Edit */}
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => navigate("/main/edit-user", { state: item })}
                  sx={{ color: "black" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Change Password */}
              <Tooltip title="Change Password">
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenPasswordModal(true);
                    setChangingPasswordUserId(item._id);
                  }}
                  sx={{ color: "black" }}
                >
                  <ChangeCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* User Name */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>User Name:</b>
            <Box sx={{ fontSize: 14 }}>{item.userName || "-"}</Box>
          </Box>

          {/* Email */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Email:</b>
            <Box sx={{ fontSize: 14 }}>{item.email || "-"}</Box>
          </Box>

          {/* Role */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Role:</b>
            <Box sx={{ fontSize: 14 }}>{item.role || "-"}</Box>
          </Box>

          {/* Allow Companies */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              Allow Companies:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.allowCompanies || "-"}</Box>
          </Box>

          {/* Allow Invoices */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              Allow Invoices:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.allowInvoices || "-"}</Box>
          </Box>

          {/* Active Date */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Active Date:</b>
            <Box sx={{ fontSize: 14 }}>{item.activeDate || "-"}</Box>
          </Box>

          {/* Expiry Date */}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Expiry Date:</b>
            <Box sx={{ fontSize: 14 }}>{item.expireDate || "-"}</Box>
          </Box>

          {/* Subscription Interval */}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Subscription:</b>
            <Box sx={{ fontSize: 14 }}>{item.subscriptionInterval || "-"}</Box>
          </Box>

          {/* App Mode */}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>App Mode:</b>
            <Box sx={{ fontSize: 14 }}>{appModeLabels(item.appMode)}</Box>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap", // important for mobile
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall, // mobile
                sm: globalStyle.headingFontSize, // tablet+
              },
              lineHeight: 1.2,
            }}
          >
            Users List
          </Box>

          {/* Optional subtitle (mobile friendly) */}
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: {
                xs: 12,
                sm: 14,
              },
              color: "#666",
            }}
          >
            Manage and view all Users records
          </Box>
        </Box>
      </Box>
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingUserList}
          searchLabel="Users"
        >
          {renderMobileUserCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={userList || []}
          manualPagination
          rowCount={totalResults}
          state={{
            columnOrder: [
              "_id",
              "userName",
              "email",
              "role",
              "lastLogin",
              "allowCompanies",
              "allowInvoices",
              "activeDate",
              "expireDate",
              "subscriptionInterval",
              "appMode",
              "actions",
            ],
            columnPinning: {
              right: ["actions"],
            },
            isLoading: isGettingUserList || isBlockingUser,
            pagination,
            globalFilter,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor: globalStyle.bgColor,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none",
              overflow: "visible",
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
        />
      )}
      <BlockUserModal
        loader={isBlockingUser}
        open={Boolean(blockingUser)}
        setOpen={(value) => !value && setBlockingUser(null)}
        userName={blockingUser?.userName}
        handleSubmit={(reason, closeModal) => {
          dispatchChangeUserStatus(
            blockingUser._id,
            { enable: false, blockReason: reason },
            () => {
              closeModal();
              setBlockingUser(null);
            },
          );
        }}
      />
      <ChangePasswordModal
        loader={isChangingUserPassword}
        openModalPassword={openModalPassword}
        setOpenPasswordModal={setOpenPasswordModal}
        isFromMui={false}
        handleSubmit={(value, closeModal) => {
          dispatch(
            changeUserPassword(
              changingPasswordUserId,
              value,
              handleVisible,
              handleDangerVisible,
              () => {
                closeModal();
                setChangingPasswordUserId(null);
              },
            ),
          );
        }}
      />
    </Box>
  );
};

export default ViewUser;
